import type { UseQueryOptions } from "@tanstack/react-query";

import type {
    PlatformContractUserIProEngagementKeywordGroup,
    PlatformContractUserProShareResponse,
    PlatformContractUserViewSharingType,
} from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { apiClient } from "app/modules/api-client";
import type { IKeywordGroupDataForTracking } from "app/pages/keyword-analysis/KeywordsGroupsTracking";
import { KeywordsGroupsTracking } from "app/pages/keyword-analysis/KeywordsGroupsTracking";
import queryClient from "single-spa/react-app/query-client";

import type {
    ProKeywordGroup,
    IUpdateKeywordGroupPayload,
    ShareKeywordGroupPayload,
    CreateKeywordGroupPayload,
    DuplicateKeywordGroupPayload,
} from "../keywordGroups.apiClient.types";
import { KEYWORD_GROUP_PREFIX } from "../utils/keywordGroupsApiClientTransforms";

/**
 * @internal
 * do not use this function in the app
 */
export const keywordGroupsApiClientKeys = {
    all: ["keywordGroups", "apiClient"] as const,
    user: () => [...keywordGroupsApiClientKeys.all, "user"] as const,
    shared: () => [...keywordGroupsApiClientKeys.all, "shared"] as const,
    byId: (id: string) => [...keywordGroupsApiClientKeys.all, "byId", id] as const,
} as const;

const fetchUserKeywordGroups = async (): Promise<PlatformContractUserIProEngagementKeywordGroup[]> => {
    try {
        const response = await apiClient.Userdata.userdataKeywordGroupsList({
            engagementData: false,
            orderByLastUpdated: false,
        });

        // Set shared groups in query client (matching legacy behavior)
        await queryClient.setQueryData(
            keywordGroupsApiClientKeys.shared(),
            response.SharedGroups?.filter(Boolean) || [],
        );

        return response.OwnerGroups?.filter(Boolean) || [];
    } catch (error) {
        Sentry.errorTracker({
            message: "Error fetching user keyword groups",
            e: error,
            level: "error",
        });
        return [];
    }
};

const fetchSharedKeywordGroups = async (): Promise<ProKeywordGroup[]> => {
    try {
        const response = await apiClient.Userdata.userdataKeywordGroupsSharedList();
        return Array.isArray(response) ? response : [];
    } catch (error) {
        Sentry.errorTracker({
            message: "Error fetching shared keyword groups",
            e: error,
            level: "error",
        });
        return [];
    }
};

const fetchKeywordGroupById = async (id: string): Promise<ProKeywordGroup | undefined> => {
    if (!id) {
        return;
    }

    try {
        const [userGroups, sharedGroups] = await Promise.all([
            queryClient.fetchQuery(keywordGroupsApiClientOptions.user() as UseQueryOptions<ProKeywordGroup[]>),
            queryClient.fetchQuery(keywordGroupsApiClientOptions.shared() as UseQueryOptions<ProKeywordGroup[]>),
        ]);

        const userGroup = userGroups.find((group) => group.Id === id);
        if (userGroup) {
            return userGroup;
        }

        const sharedGroup = sharedGroups.find((group) => group.Id === id);
        return sharedGroup;
    } catch (error) {
        Sentry.errorTracker({
            message: "Error fetching keyword group by id",
            e: error,
            level: "error",
            params: {
                extra: { id },
            },
        });
        return;
    }
};

/**
 * @internal
 * do not use this function in the app
 */
export const createKeywordGroup = async (group: CreateKeywordGroupPayload): Promise<void> => {
    const response = await apiClient.Userdata.userdataKeywordGroupsCreate({ returnOnlyNewGroup: false }, group);
    const allGroups: ProKeywordGroup[] = response;
    const added = allGroups?.find((kg) => kg.Name === group.Name);

    if (added) {
        KeywordsGroupsTracking.genericGroupTracker.onCreated(added as IKeywordGroupDataForTracking);
    }
};

/**
 * @internal
 * do not use this function in the app
 */
export const updateKeywordGroup = async (group: IUpdateKeywordGroupPayload): Promise<void> => {
    await apiClient.Userdata.userdataKeywordGroupsUpdate(group);
};

/**
 * @internal
 * do not use this function in the app
 */
export const deleteKeywordGroup = async (id: string, name: string, keywords: string[]): Promise<ProKeywordGroup[]> => {
    const cleanId = id.startsWith(KEYWORD_GROUP_PREFIX) ? id.replace(KEYWORD_GROUP_PREFIX, "") : id;
    const response = await apiClient.Userdata.userdataKeywordGroupsDelete({ id: cleanId });
    // eslint-disable-next-line @typescript-eslint/naming-convention
    KeywordsGroupsTracking.genericGroupTracker.onDeleted({ Id: id, Name: name, Keywords: keywords });
    return Array.isArray(response) ? response : [];
};

/**
 * @internal
 * for internal use only
 * do not use this function in the app
 */
export const shareKeywordGroup = async (
    groupId: string,
    payload: ShareKeywordGroupPayload,
): Promise<PlatformContractUserProShareResponse> => {
    const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter((entry) => entry[1] !== null && entry[1] !== undefined),
    );

    const payloadWithViewSharingType = {
        ...cleanPayload,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ViewSharingType: 0 as PlatformContractUserViewSharingType,
    } as PlatformContractUserProShareResponse;

    const response = await apiClient.Userdata.userdataKeywordGroupsShareUpdate(groupId, payloadWithViewSharingType);
    return response;
};

/**
 * @internal
 * do not use this function in the app
 */
export const removeKeywordGroupSharing = async (groupId: string): Promise<boolean> => {
    const response = await apiClient.Userdata.userdataKeywordGroupsShareDelete(groupId);
    return response;
};

/**
 * @internal
 * do not use this function in the app
 */
export const duplicateKeywordGroup = async (payload: DuplicateKeywordGroupPayload): Promise<ProKeywordGroup> => {
    const response = await apiClient.Userdata.userdataKeywordGroupsDuplicateUpdate(payload);
    return response;
};

const THIRTY_MINUTES = 30 * 60 * 1000;

/**
 * @internal
 * do not use this function in the app
 */
export const keywordGroupsApiClientOptions: Record<string, (...args: any[]) => UseQueryOptions<any>> = {
    user: () => ({
        queryKey: keywordGroupsApiClientKeys.user(),
        queryFn: fetchUserKeywordGroups,
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
        retry: 1,
    }),

    shared: () => ({
        queryKey: keywordGroupsApiClientKeys.shared(),
        queryFn: fetchSharedKeywordGroups,
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
        retry: 1,
    }),

    byId: (id: string) => ({
        queryKey: keywordGroupsApiClientKeys.byId(id),
        queryFn: () => fetchKeywordGroupById(id),
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
        retry: 1,
        enabled: !!id,
    }),
} as const;
